import * as React from 'react'
import Image from 'next/image'
import Link from 'next/link'

import Divider from '@mui/material/Divider'
import MuiCard from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'

import Button from 'components/atomic/atoms/button'
import Text from 'components/atomic/atoms/text'

import Routes from 'utils/routes'
import CardActionArea from '@mui/material/CardActionArea'

const Card = React.memo(({ title, logo, to }: { title: string; logo: string; to: string }) => {
    return (
        <Link href={to}>
            <a>
                <MuiCard sx={{ height: '100%' }}>
                    <CardActionArea sx={{ p: 1, height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Image
                                alt="Path icon"
                                unoptimized
                                width={40}
                                height={40}
                                objectFit="contain"
                                style={{ opacity: 0.7, flex: 'none' }}
                                src={logo}
                            />
                            <Text fontWeight={'bold'}>{title}</Text>
                        </Stack>
                    </CardActionArea>
                </MuiCard>
            </a>
        </Link>
    )
})

const CatalogDropdown = (props: { onHover: (a: boolean) => void }) => {
    const courses = [
        {
            title: 'Linguaggi',
            items: [
                {
                    text: 'Python',
                    to: Routes.Category('python'),
                },
                {
                    text: 'Javascript',
                    to: Routes.Category('javascript'),
                },
                {
                    text: 'HTML & CSS',
                    to: Routes.Category('html-css'),
                },
            ],
        },
        {
            title: '',
            items: [
                {
                    text: 'SQL',
                    to: Routes.Category('sql'),
                },
                {
                    text: 'C++',
                    to: Routes.Category('c-plus-plus'),
                },
                {
                    text: 'Java',
                    to: Routes.Category('java'),
                },
            ],
        },
        {
            title: 'Argomenti',
            items: [
                {
                    text: 'Web Development',
                    to: Routes.Category('web-development'),
                },
                {
                    text: 'Game Development',
                    to: Routes.Category('game-development'),
                },
                {
                    text: 'Artificial Intelligence (AI)',
                    to: Routes.Category('machine-learning'),
                },
            ],
        },
        {
            title: '',
            items: [
                {
                    text: 'Cybersecurity',
                    to: Routes.Category('cybersecurity'),
                },
                {
                    text: 'Machine Learning',
                    to: Routes.Category('machine-learning'),
                },
                {
                    text: 'Mobile Development',
                    to: Routes.Category('mobile-development'),
                },
            ],
        },
    ]

    const paths = [
        {
            title: 'Full-Stack Engineer',
            to: Routes.Path('full-stack-engineer-career-path'),
            logo: '/assets/paths/path-icon-1.png',
        },
        {
            title: 'Back-End Engineer',
            to: Routes.Path('back-end-engineer-career-path'),
            logo: '/assets/paths/path-icon-2.png',
        },
        {
            title: 'Front-End Engineer',
            to: Routes.Path('front-end-engineer-career-path'),
            logo: '/assets/paths/path-icon-3.png',
        },
        {
            title: 'Software Engineer',
            to: Routes.Path('computer-science'),
            logo: '/assets/paths/path-icon-4.png',
        },
        {
            title: 'AI Engineer',
            to: Routes.Path('machine-learning-engineer'),
            logo: '/assets/paths/path-icon-5.png',
        },
        {
            title: 'IOS Developer',
            to: Routes.Path('ios-developer'),
            logo: '/assets/paths/path-icon-6.png',
        },
    ]

    return (
        <Box
            sx={{ marginTop: 1, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}
            onMouseEnter={() => props.onHover(true)}
            onMouseLeave={() => props.onHover(false)}>
            <Box sx={{ py: { xs: 4, md: 6 } }}>
                <Grid container spacing={{ xs: 4, md: 6 }}>
                    <Grid item xs={12} md={4}>
                        <Stack direction="column">
                            <Text variant="h6" color="text.primary" gutterBottom>
                                I corsi piu popolari
                            </Text>
                            <Text width={'80%'} variant="subtitle2">
                                Scopri tutti i corsi sugli argomenti che piu ti interessano
                            </Text>
                            <Box mt={3}>
                                <Link href={Routes.Catalog}>
                                    <a>
                                        <Button variant="outlined">Vai al catalogo</Button>
                                    </a>
                                </Link>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={4} justifyContent="space-evenly">
                            {courses.map((column, index) => (
                                <Grid
                                    item
                                    xs={6}
                                    md={3}
                                    key={column.title + index}
                                    display={'flex'}
                                    justifyContent={'end'}
                                    flexDirection={'column'}>
                                    <Text variant="h6" color="text.primary" gutterBottom>
                                        {column.title}
                                    </Text>
                                    <Stack direction="column" justifyContent={'end'} spacing={1} mt={1}>
                                        {column.items.map((item) => (
                                            <Link href={item.to} key={item.text} passHref>
                                                <a>
                                                    <Text variant="subtitle2">{item.text}</Text>
                                                </a>
                                            </Link>
                                        ))}
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Divider />
            <Box sx={{ py: { xs: 4, md: 6 } }}>
                <Grid container spacing={{ xs: 4, md: 6 }}>
                    <Grid item xs={12} md={4}>
                        <Stack direction="column" spacing={0}>
                            <Text variant="h6" color="text.primary" gutterBottom>
                                Percorsi di carriera
                            </Text>
                            <Text width={'80%'} variant="subtitle2">
                                Esplora i percorsi creati per imparare una professione e trovare lavoro
                            </Text>
                            <Box mt={3}>
                                <Link href={Routes.Catalog}>
                                    <a>
                                        <Button variant="outlined">Inizia una carriera</Button>
                                    </a>
                                </Link>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={1} justifyContent="space-evenly">
                            {paths.map((item) => (
                                <Grid item xs={6} md={4} key={item.title}>
                                    <Card title={item.title} to={item.to} logo={item.logo} />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}

export default CatalogDropdown
